import * as Sentry from '@sentry/browser';
import { ActionKeys } from '~/actions/constants';
import * as loginActions from '~/actions/login.actions';
import { push } from 'connected-react-router';
import * as Cookies from 'js-cookie';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  forgotPassword,
  generate2FA as backendGenerate2FA,
  login as backendLogin,
  LoginFailures,
  TwoFactorMessages,
} from '~/services/login.service';
import { getTopLevelDomain } from '~/utils/url';

export function* login(action: ReturnType<typeof loginActions.loginStart>) {
  const { email, gRecaptchaResponse, next, password } = action.payload;

  try {
    const response = yield call(backendLogin, email, password, gRecaptchaResponse);
    const { status, success, reason } = response;

    if (success && reason === 'success') {
      yield put(loginActions.loginSuccess(next));
    } else {
      if (!success && status === 401) {
        switch (reason) {
          case LoginFailures.CHALLENGE_2FA:
            yield put(loginActions.verify2FAStart());
            const challengeUrl = next
              ? `/challenge?next=${encodeURIComponent(next)}`
              : '/challenge';
            yield put(push(challengeUrl));
            break;
          case LoginFailures.LOGIN_FAILED:
            yield put(loginActions.loginError(reason));
            break;
          case LoginFailures.EXCESSIVE_LOGINS:
          case LoginFailures.INVALID_CAPTCHA:
          case LoginFailures.INVALID_CREDS:
            yield put(loginActions.loginError(reason));
            const loginUrl = next ? `/login?next=${encodeURIComponent(next)}` : '/login';
            yield put(push(loginUrl));
            break;
        }
      } else {
        yield put(loginActions.loginError('Login Unsuccessful'));
      }
    }
  } catch (error) {
    yield put(loginActions.loginError(error as string));
    yield call(Sentry.captureException, error);
  }
}

export function* loginSuccessful(action: ReturnType<typeof loginActions.loginSuccess>) {
  const { next } = action.payload;
  const cookieDomain = getTopLevelDomain(window.location.href);

  // setting cookies for existing users until experiment launch
  if (new Date() < new Date('11/08/2023')) {
    yield call(Cookies.set, 'is_existing_user', 'true', {
      expires: 90,
      domain: `.${cookieDomain}`,
    });
  }

  if (next) {
    const absolute = new RegExp(/^https?:\/\//i);
    if (absolute.test(next)) {
      window.location.href = next;
    } else {
      yield put(push(next));
    }
  } else {
    yield put(push('/'));
  }
}

export function* loginWith2FA(action: ReturnType<typeof loginActions.verify2FASubmit>) {
  const { email, challengeCode, gRecaptchaResponse, next, password } = action.payload;

  try {
    const { reason, success, status } = yield call(
      backendLogin,
      email,
      password,
      gRecaptchaResponse,
      challengeCode,
    );
    if (success) {
      yield put(loginActions.verify2FASuccess());
      yield put(loginActions.loginSuccess(next));
    } else if (!success && status === 401) {
      switch (reason) {
        case LoginFailures.INVALID_CREDS:
        case LoginFailures.EXCESSIVE_LOGINS:
          yield put(loginActions.loginError(reason));
          const loginUrl = next ? `/login?next=${encodeURIComponent(next)}` : '/login';
          yield put(push(loginUrl));
          break;
        case LoginFailures.CHALLENGE_2FA:
        case LoginFailures.EXCESSIVE_2FA:
        case LoginFailures.EXPIRED_CODE:
        case LoginFailures.INVALID_CAPTCHA:
        case LoginFailures.INVALID_CODE:
          yield put(loginActions.verify2FAError(reason));
          break;
      }
    } else {
      yield put(loginActions.verify2FAError('Verification Unsuccessful'));
    }
  } catch (error) {
    yield put(loginActions.verify2FAError(error as string));
    yield call(Sentry.captureException, error);
  }
}

export function* resetPassword(action: ReturnType<typeof loginActions.resetPasswordSubmit>) {
  const { email, gRecaptchaResponse } = action.payload;

  try {
    const { reason, success, status } = yield call(forgotPassword, email, gRecaptchaResponse);
    if (success) {
      yield put(loginActions.resetPasswordSuccess());
      yield put(push('/sent'));
    } else if (!success && status === 401) {
      yield put(loginActions.resetPasswordError(reason));
    } else {
      yield put(loginActions.resetPasswordError('Error submitting reset password action'));
    }
  } catch (error) {
    yield put(loginActions.resetPasswordError(error as string));
    yield call(Sentry.captureException, error);
  }
}

export function* generate2FA(action: ReturnType<typeof loginActions.resend2FAEmail>) {
  try {
    const { email } = action.payload;

    const { success } = yield call(backendGenerate2FA, email);
    if (success) {
      yield put(loginActions.resend2FASuccess(TwoFactorMessages.RESEND_2FA_SUCCESS));
    } else {
      yield put(loginActions.resend2FAError('Error requesting two-factor token'));
    }
  } catch (error) {
    yield put(loginActions.resend2FAError(error as string));
    yield call(Sentry.captureException, error);
  }
}

export default [
  takeLatest(ActionKeys.LOGIN_SUBMIT, login),
  takeLatest(ActionKeys.LOGIN_SUCCESS, loginSuccessful),
  takeLatest(ActionKeys.RESET_PASSWORD_SUBMIT, resetPassword),
  takeLatest(ActionKeys.VERIFY_2FA_SUBMIT, loginWith2FA),
  takeLatest(ActionKeys.RESEND_2FA_SUBMIT, generate2FA),
];
